/**
 * A number's greeting or voicemail prompt as the Company's own recording
 * rather than the words said by a computer voice (migration 076).
 *
 * With none, it hangs under the box of words as a microphone. With one,
 * it is the field: the box is not drawn (`TextAreaField`'s `replaced`),
 * because the recording is what callers hear and a box beside it would
 * have to be explained away. The words stay stored and are said whenever
 * the recording cannot be played, so a recording never leaves anyone
 * listening to silence; removing it brings the box back. Recorded here
 * from the microphone and nowhere else — no file is chosen — and turned
 * into the telephone's own format in the browser before it goes up
 * (ADR-0035). Stopping is the commit; removing one asks first.
 *
 * Built from what the admin already draws audio with: the call's own
 * player (`Player`, as on a call and the job thread), the icon buttons
 * Settings → Questions' rows use (`IconAction`), the microphone the phone's
 * mute uses, a recorder's square to stop, and the bin the logo row uses.
 */
import { useEffect, useRef, useState } from "react";
import { Player } from "../../components/arrivals/CallParts";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { InlineBanner } from "../../components/ui/InlineBanner";
import { MicIcon, StopIcon, TrashIcon } from "../../components/ui/icons";
import { errorMessage } from "../../lib/error-message";
import {
  canRecordHere,
  MAX_PROMPT_SECONDS,
  recordFromMic,
  toTelephoneWav,
  type MicRecording,
} from "../../lib/prompt-audio";
import {
  removePromptRecording,
  uploadPromptRecording,
  type PhoneNumber,
  type RecordedPrompt,
} from "../../lib/settings-api";
import { peaksOf, type Decoded } from "../../lib/waveform";
import { useAction } from "../../lib/use-action";
import { IconAction } from "./InquiryQuestions";
import styles from "./PromptRecording.module.css";

/**
 * Bars already read, by URL: a recording just made was decoded on the
 * way up, and one opened again this session was decoded the first time.
 * A URL names the bytes, so what was read for it never goes stale.
 */
const heard = new Map<string, Decoded>();

const COPY: Record<RecordedPrompt, { noun: string; none: string }> = {
  greeting: {
    noun: "greeting",
    // The greeting carries the recording notice (Privacy Act IPP 3), and
    // a recording replaces the words that said it.
    none: "Or record it in your own voice. Say that the call is recorded.",
  },
  voicemail: {
    noun: "voicemail prompt",
    none: "Or record it in your own voice.",
  },
};

/** How many bars the waveform draws, and how often they move. */
const WAVE_BARS = 9;
const WAVE_STEP_MS = 100;
/**
 * How far the drawn level moves toward the heard one each step: up
 * quicker than down, as a meter does, and both slow enough that a voice
 * is a swell rather than a flicker.
 */
const WAVE_RISE = 0.35;
const WAVE_FALL = 0.12;
/** Each bar's share of the level: tallest in the middle, as a voice looks. */
const WAVE_SHAPE = Array.from({ length: WAVE_BARS }, (_, i) => {
  const off = (i - (WAVE_BARS - 1) / 2) / ((WAVE_BARS - 1) / 2);
  return 1 - 0.6 * off * off;
});

/**
 * The microphone hearing them: a small green waveform that stays where it
 * is and rises and falls with their voice, flat while they are quiet. In
 * place of a red dot, which says the microphone is on but not that it is
 * hearing anything — a muted or wrong input looks the same as a working
 * one until the recording is played back. Each bar is the level shaped to
 * the middle with a little of its own wobble, so speech reads as a voice
 * and not as one block going up and down. Heights are written straight
 * to the bars rather than through state: twenty renders a second of the
 * whole row for a decoration is the wrong trade.
 */
function LevelWave({ level }: { level?: () => number }) {
  const bars = useRef<(HTMLSpanElement | null)[]>([]);
  useEffect(() => {
    if (!level) return;
    let shown = 0;
    // Each bar's wobble drifts rather than jumps, so the bars stay apart
    // without twitching.
    const wobble = WAVE_SHAPE.map(() => 1);
    const tick = setInterval(() => {
      const heard = level();
      shown += (heard - shown) * (heard > shown ? WAVE_RISE : WAVE_FALL);
      WAVE_SHAPE.forEach((shape, i) => {
        wobble[i] += (0.85 + 0.15 * Math.random() - wobble[i]) * 0.3;
        const bar = bars.current[i];
        if (bar)
          bar.style.transform = `scaleY(${Math.max(0.15, shown * shape * wobble[i])})`;
      });
    }, WAVE_STEP_MS);
    return () => clearInterval(tick);
  }, [level]);
  return (
    <span className={styles.wave} aria-hidden="true">
      {WAVE_SHAPE.map((_, i) => (
        <span
          key={i}
          ref={(el) => {
            bars.current[i] = el;
          }}
          className={styles.waveBar}
        />
      ))}
    </span>
  );
}

function clock(secs: number): string {
  const s = Math.floor(secs);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function PromptRecording({
  number,
  prompt,
  onChanged,
}: {
  number: PhoneNumber;
  prompt: RecordedPrompt;
  /** The number as the server now holds it. */
  onChanged: (next: PhoneNumber) => void;
}) {
  const url =
    prompt === "greeting"
      ? number.greeting_recording_url
      : number.voicemail_recording_url;
  const copy = COPY[prompt];

  const save = useAction();
  const removal = useAction();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [mic, setMic] = useState<MicRecording | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [decoded, setDecoded] = useState<Decoded | null>(() =>
    url ? (heard.get(url) ?? null) : null,
  );
  // The one being made, for the timer that ends it and for unmount.
  const live = useRef<MicRecording | null>(null);

  // The stored recording's bars, read once; where the browser will not
  // fetch or decode it, the player is drawn flat and still plays.
  useEffect(() => {
    if (!url) {
      setDecoded(null);
      return;
    }
    const held = heard.get(url);
    if (held) {
      setDecoded(held);
      return;
    }
    let current = true;
    setDecoded(null);
    fetch(url)
      .then((res) => (res.ok ? res.blob() : null))
      .then((blob) => (blob ? peaksOf(blob) : null))
      .then((read) => {
        if (!read) return;
        heard.set(url, read);
        if (current) setDecoded(read);
      })
      .catch(() => {});
    return () => {
      current = false;
    };
  }, [url]);

  // A recording left running is the microphone left on.
  useEffect(() => () => live.current?.cancel(), []);

  /** Convert, upload, and take the number back: the one commit. */
  const keep = async (audio: Blob) => {
    await save.run(async () => {
      const converted = await toTelephoneWav(audio);
      const next = await uploadPromptRecording(number.id, prompt, converted.wav);
      const nextURL =
        prompt === "greeting"
          ? next.greeting_recording_url
          : next.voicemail_recording_url;
      if (nextURL) heard.set(nextURL, converted);
      onChanged(next);
    });
  };

  const startRecording = async () => {
    save.setError(null);
    try {
      const started = await recordFromMic();
      live.current = started;
      setElapsed(0);
      setMic(started);
    } catch (err) {
      save.setError(errorMessage(err));
    }
  };

  const stopRecording = async () => {
    const current = live.current;
    if (!current) return;
    live.current = null;
    setMic(null);
    await keep(await current.stop());
  };

  // The clock while it records, and the minute that ends it: longer is
  // refused by the server, so it is stopped here rather than thrown away.
  useEffect(() => {
    if (!mic) return;
    const began = Date.now();
    const tick = setInterval(() => {
      const secs = (Date.now() - began) / 1000;
      setElapsed(secs);
      if (secs >= MAX_PROMPT_SECONDS - 0.5) void stopRecording();
    }, 250);
    return () => clearInterval(tick);
    // stopRecording reads the ref, not the render it was made in.
  }, [mic]);

  const remove = async () => {
    await removal.run(async () => {
      onChanged(await removePromptRecording(number.id, prompt));
      setConfirmRemove(false);
    });
  };

  const busy = save.working || removal.working;
  const recording = mic !== null;

  return (
    <div className={styles.recording}>
      <div className={styles.row}>
        <div className={styles.body}>
          {recording ? (
            <p className={styles.status} role="status">
              <LevelWave level={mic.level} />
              Recording {clock(elapsed)} of {clock(MAX_PROMPT_SECONDS)}
            </p>
          ) : save.working ? (
            <p className={styles.status} role="status">
              Saving your recording…
            </p>
          ) : url ? (
            <Player
              src={url}
              bars={decoded?.bars ?? []}
              seconds={decoded?.seconds ?? 0}
              counted={false}
            />
          ) : canRecordHere() ? (
            <p className={styles.note}>{copy.none}</p>
          ) : null}
        </div>

        <span className={styles.actions}>
          {/* Drawn only where the browser can reach a microphone: an
              older WebView or a page off HTTPS gets the words alone. */}
          {canRecordHere() &&
            (recording ? (
              <IconAction
                label="Stop recording"
                disabled={false}
                onClick={() => void stopRecording()}
              >
                <StopIcon className={styles.icon} />
              </IconAction>
            ) : (
              <IconAction
                label={`Record the ${copy.noun}`}
                disabled={busy}
                onClick={() => void startRecording()}
              >
                <MicIcon className={styles.icon} />
              </IconAction>
            ))}
          {url && (
            <IconAction
              label={`Remove the recorded ${copy.noun}`}
              disabled={busy || recording}
              onClick={() => setConfirmRemove(true)}
            >
              <TrashIcon className={styles.icon} />
            </IconAction>
          )}
        </span>
      </div>

      {save.error && <InlineBanner variant="error">{save.error}</InlineBanner>}

      <ConfirmModal
        open={confirmRemove}
        title={`Remove the recorded ${copy.noun}?`}
        message="Callers hear the typed words again."
        confirmLabel="Remove recording"
        loading={removal.working}
        error={removal.error}
        onConfirm={() => void remove()}
        onCancel={() => {
          setConfirmRemove(false);
          removal.setError(null);
        }}
      />
    </div>
  );
}
